import { planType } from '../../modules/shared/subscription-constants'

const dataLayerPush = payload => {
  if (!window.dataLayer) {
    window.dataLayer = []
  }
  window.dataLayer.push(payload)
}

const variationText = (plan) => {
  if (plan === planType.TVOD) {
    return 'signup without subscription'
  }
  const convertPlan = plan && plan.toLowerCase()
  return `signup with ${convertPlan} subscription`
}

/**
 * This function hashes the input text using SHA-256 algorithm
 * and returns the hash as a hexadecimal string.
 * If the input is empty or undefined, it returns undefined.
 *
 * @param {string} text - The input text to be hashed
 * @returns {Promise<string|undefined>} - The SHA-256 hash of the input text as a hexadecimal string or undefined
 */
const sha256 = async (text) => {
  if (!text) return undefined

  const normalized = text.trim().toLowerCase()
  const data = new TextEncoder().encode(normalized)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * This function hashes the user's email and phone number,
 * and pushes the user login data to the data layer
 *
 * @param {string} userId - sub from JWT(decodedJwt.sub) or accountId
 * @param {object} userData - The user's data including email, phone numbers, name, and surname
 * @param {boolean} [isUserDataUpdate=false] - Indicates if this is a user data updater
 */
const userLoginDataLayer = async (userId, {
  email, phoneNumbers, name, surname
} = {}, isUserDataUpdate = false) => {
  const userEmailHashed = await sha256(email)
  const userPhoneHashed = await sha256(phoneNumbers?.mobile)
  const userFirstName = name
  const userLastName = surname

  const eventType = isUserDataUpdate ? 'custom.user.update' : 'custom.user.login'

  dataLayerPush({
    event: eventType,
    content: {
      environment: 'production' // We differentiate envs on GTM IDs
    },
    user: {
      userId,
      userStatus: 'signed in',
      userEmailHashed,
      userPhoneHashed,
      userFirstName,
      userLastName
    }
  })
}

/**
 * This function pushes the user logout data to the data layer
 * It sets the userId, userEmailHashed, userPhoneHashed, userFirstName, and userLastName to undefined
 * It sets the userStatus to 'guest'
 */
const userLogoutDataLayer = () => {
  dataLayerPush({
    event: 'custom.user.logout',
    content: {
      environment: 'production' // We differentiate envs on GTM IDs
    },
    user: {
      userId: undefined,
      userStatus: 'guest',
      userEmailHashed: undefined,
      userPhoneHashed: undefined,
      userFirstName: undefined,
      userLastName: undefined
    }
  })
}

const ctaClick = label => {
  dataLayerPush({
    event: 'custom.navigation.cta',
    group: 'cta',
    label
  })
}

const playbackStart = (title, videoType, { season, episode } = {}) => {
  dataLayerPush({
    event: 'custom.video.interactions',
    group: title,
    label: 'play',
    data: {
      videoType,
      season,
      episode
    }
  })
}

const stepView = {
  event: 'custom.form.step.view',
  group: 'signup'
}

const signup = {
  start: () => {
    dataLayerPush({
      event: 'custom.form.start',
      group: 'signup'
    })
  },

  planSelectionSeen: (sequence = 2) => {
    dataLayerPush({
      ...stepView,
      step: {
        sequence,
        label: 'choose a plan',
        variation: ''
      }
    })
  },

  createAccountSeen: (sequence = 1) => {
    dataLayerPush({
      ...stepView,
      step: {
        sequence,
        label: 'details',
        variation: ''
      }
    })
  },

  subscriptionPaymentSeen: (plan = 'monthly') => {
    dataLayerPush({
      ...stepView,
      step: {
        sequence: 3,
        label: 'payment details',
        variation: variationText(plan)
      }
    })
  },

  addCreditCardSeen: () => {
    dataLayerPush({
      ...stepView,
      step: {
        sequence: 3,
        label: 'payment details',
        variation: 'signup without subscription'
      }
    })
  },

  subscriptionConfirmationSeen: (plan = 'monthly') => {
    dataLayerPush({
      ...stepView,
      step: {
        sequence: 4,
        label: 'signup complete',
        variation: variationText(plan)
      }
    })
  },

  rentalOnlyConfirmationSeen: () => {
    dataLayerPush({
      ...stepView,
      step: {
        sequence: 4,
        label: 'signup complete',
        variation: 'signup without subscription'
      }
    })
  },

  fieldComplete: (fieldName, data, sequence = 1) => {
    dataLayerPush({
      event: 'custom.form.field.complete',
      group: 'signup',
      label: fieldName,
      step: {
        sequence,
        label: 'details',
        variation: ''
      },
      data
    })
  },

  validationError: (errorMessage, sequence = 1) => {
    dataLayerPush({
      event: 'custom.form.error.view',
      group: 'signup',
      label: errorMessage,
      step: {
        sequence,
        label: 'details',
        variation: ''
      }
    })
  },

  completeWithSubscription: (userId, orderId, isCreditCard, plan = 'monthly') => {
    dataLayerPush({
      event: 'custom.form.complete',
      group: 'signup',
      data: {
        userId,
        orderId,
        planName: plan,
        creditCard: isCreditCard ? 'yes' : 'no',
        loggedinStatus: true,
        variation: variationText(plan)
      }
    })
  },

  completeWithoutSubscription: (userId) => {
    dataLayerPush({
      event: 'custom.form.complete',
      group: 'signup',
      data: {
        userId,
        creditCard: 'no',
        loggedinStatus: true,
        variation: 'signup without subscription'
      }
    })
  }
}

export {
  ctaClick,
  playbackStart,
  signup,
  userLoginDataLayer,
  userLogoutDataLayer
}
